import React, { useEffect, useState } from "react";
import api from "../api/client";
import { useSort, sortRows, SortableTh } from "../components/SortableTh";
import { formatDateTime } from "../utils/datetime";

type Provider = "connectwise" | "autotask" | "halo";

type Integration = {
  id: number;
  name: string;
  provider: Provider;
  base_url: string;
  enabled: boolean;
  sync_companies: boolean;
  sync_tickets: boolean;
  has_credentials: boolean;
  last_sync_at: string | null;
  last_sync_status: string | null;
  last_sync_error: string | null;
};

// Per-provider credential fields. These are write-only: stored secrets are
// never returned by the API (the list exposes has_credentials instead).
const CRED_FIELDS: Record<Provider, { key: string; label: string }[]> = {
  connectwise: [
    { key: "company_id", label: "Company ID" },
    { key: "public_key", label: "Public key" },
    { key: "private_key", label: "Private key" },
    { key: "client_id", label: "Client ID" },
  ],
  autotask: [
    { key: "integration_code", label: "API integration code" },
    { key: "username", label: "API user name" },
    { key: "secret", label: "API secret" },
  ],
  halo: [
    { key: "client_id", label: "Client ID" },
    { key: "client_secret", label: "Client secret" },
    { key: "scope", label: "Scope (optional, default: all)" },
  ],
};

const PROVIDER_LABELS: Record<Provider, string> = {
  connectwise: "ConnectWise Manage",
  autotask: "Autotask (Datto)",
  halo: "HaloPSA",
};

export default function PSAIntegrations() {
  const [rows, setRows] = useState<Integration[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<Integration | null>(null);
  const [showModal, setShowModal] = useState(false);
  // Per-row transient status for test/sync buttons.
  const [actionMsg, setActionMsg] = useState<Record<number, string>>({});
  const iSort = useSort("name");

  const refresh = async () => {
    try {
      const r = await api.get("/api/v1/psa-integrations");
      setRows(r.data);
      setError(null);
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const remove = async (id: number) => {
    if (!window.confirm("Delete this PSA integration?")) return;
    try {
      await api.delete(`/api/v1/psa-integrations/${id}`);
      refresh();
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message);
    }
  };

  const test = async (id: number) => {
    setActionMsg((m) => ({ ...m, [id]: "Testing…" }));
    try {
      const r = await api.post(`/api/v1/psa-integrations/${id}/test`);
      setActionMsg((m) => ({
        ...m,
        [id]: r.data.ok ? "Connection OK" : `Failed: ${r.data.error}`,
      }));
    } catch (e: any) {
      setActionMsg((m) => ({ ...m, [id]: e?.response?.data?.detail || e?.message }));
    }
  };

  const sync = async (id: number) => {
    setActionMsg((m) => ({ ...m, [id]: "Syncing…" }));
    try {
      const r = await api.post(`/api/v1/psa-integrations/${id}/sync`);
      if (r.data.ok) {
        const res = r.data.result || {};
        setActionMsg((m) => ({
          ...m,
          [id]: res.skipped
            ? "Company sync disabled"
            : `Synced: ${res.companies_seen} seen, ${res.created} created, ${res.linked} linked`,
        }));
      } else {
        setActionMsg((m) => ({ ...m, [id]: `Failed: ${r.data.error}` }));
      }
      refresh();
    } catch (e: any) {
      setActionMsg((m) => ({ ...m, [id]: e?.response?.data?.detail || e?.message }));
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">External PSA Integrations</h1>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Sync companies and tickets to/from an external PSA (ConnectWise Manage,
            Autotask, or HaloPSA). Credentials are encrypted (AES-256-GCM) before storage
            and are never displayed.
          </p>
        </div>
        <button
          onClick={() => {
            setEditing(null);
            setShowModal(true);
          }}
          className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm font-medium"
        >
          + Add integration
        </button>
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/40 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-300 px-3 py-2 rounded text-sm">
          {error}
        </div>
      )}

      <div className="bg-white dark:bg-gray-800 rounded border dark:border-gray-700 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-xs uppercase text-gray-500 dark:text-gray-400 border-b dark:border-gray-700">
            <tr>
              <SortableTh label="Name" sortKey="name" sort={iSort.sort} onSort={iSort.toggle} />
              <SortableTh label="Provider" sortKey="provider" sort={iSort.sort} onSort={iSort.toggle} />
              <SortableTh label="Base URL" sortKey="base_url" sort={iSort.sort} onSort={iSort.toggle} />
              <SortableTh label="Enabled" sortKey="enabled" sort={iSort.sort} onSort={iSort.toggle} />
              <SortableTh label="Credentials" sortKey="has_credentials" sort={iSort.sort} onSort={iSort.toggle} />
              <SortableTh label="Last sync" sortKey="last_sync_at" sort={iSort.sort} onSort={iSort.toggle} />
              <th className="text-right py-2 px-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={7} className="py-8 text-center text-gray-400 dark:text-gray-500">
                  No PSA integrations yet. Add one to start syncing.
                </td>
              </tr>
            ) : (
              sortRows(rows, iSort.sort, (p, key) => {
                switch (key) {
                  case "provider":
                    return (p.provider || "").toLowerCase();
                  case "base_url":
                    return (p.base_url || "").toLowerCase();
                  case "enabled":
                    return p.enabled ? 1 : 0;
                  case "has_credentials":
                    return p.has_credentials ? 1 : 0;
                  case "last_sync_at":
                    return p.last_sync_at ? Date.parse(p.last_sync_at) : 0;
                  default:
                    return (p.name || "").toLowerCase();
                }
              }).map((p) => (
                <tr key={p.id} className="border-b dark:border-gray-700 last:border-0 align-top">
                  <td className="py-2 px-3 font-medium">{p.name}</td>
                  <td className="py-2 px-3">
                    <span className="text-xs px-2 py-0.5 rounded font-mono bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200">
                      {PROVIDER_LABELS[p.provider] || p.provider}
                    </span>
                  </td>
                  <td className="py-2 px-3 font-mono text-xs break-all">{p.base_url}</td>
                  <td className="py-2 px-3">{p.enabled ? "✓" : ""}</td>
                  <td className="py-2 px-3">
                    {p.has_credentials ? (
                      <span className="text-green-700 dark:text-green-400 text-xs">configured</span>
                    ) : (
                      <span className="text-amber-700 dark:text-amber-400 text-xs">none</span>
                    )}
                  </td>
                  <td className="py-2 px-3 text-xs text-gray-500 dark:text-gray-400">
                    {p.last_sync_at ? (
                      <>
                        <div>{formatDateTime(p.last_sync_at)}</div>
                        <div
                          className={
                            p.last_sync_status === "success"
                              ? "text-green-700 dark:text-green-400"
                              : "text-red-700 dark:text-red-400"
                          }
                        >
                          {p.last_sync_status}
                        </div>
                        {p.last_sync_error && (
                          <div className="text-red-600 dark:text-red-400">{p.last_sync_error}</div>
                        )}
                      </>
                    ) : (
                      "never"
                    )}
                    {actionMsg[p.id] && (
                      <div className="mt-1 text-blue-700 dark:text-blue-400">{actionMsg[p.id]}</div>
                    )}
                  </td>
                  <td className="py-2 px-3 text-right space-x-2 whitespace-nowrap">
                    <button
                      onClick={() => test(p.id)}
                      className="text-xs text-emerald-600 hover:underline"
                    >
                      Test
                    </button>
                    <button
                      onClick={() => sync(p.id)}
                      disabled={!p.enabled}
                      className="text-xs text-indigo-600 hover:underline disabled:opacity-40 disabled:no-underline"
                    >
                      Sync now
                    </button>
                    <button
                      onClick={() => {
                        setEditing(p);
                        setShowModal(true);
                      }}
                      className="text-xs text-blue-600 hover:underline"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => remove(p.id)}
                      className="text-xs text-red-600 hover:underline"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showModal && (
        <IntegrationModal
          editing={editing}
          onClose={() => setShowModal(false)}
          onSaved={() => {
            setShowModal(false);
            refresh();
          }}
        />
      )}
    </div>
  );
}

function IntegrationModal({
  editing,
  onClose,
  onSaved,
}: {
  editing: Integration | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(editing?.name || "");
  const [provider, setProvider] = useState<Provider>(editing?.provider || "connectwise");
  const [baseUrl, setBaseUrl] = useState(editing?.base_url || "");
  const [enabled, setEnabled] = useState(editing?.enabled ?? true);
  const [syncCompanies, setSyncCompanies] = useState(editing?.sync_companies ?? true);
  const [syncTickets, setSyncTickets] = useState(editing?.sync_tickets ?? true);
  const [creds, setCreds] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      // Only send credential keys the admin actually filled in.
      const filled: Record<string, string> = {};
      for (const f of CRED_FIELDS[provider]) {
        if (creds[f.key]) filled[f.key] = creds[f.key];
      }
      const hasCreds = Object.keys(filled).length > 0;

      if (editing) {
        const body: any = {
          name,
          base_url: baseUrl,
          enabled,
          sync_companies: syncCompanies,
          sync_tickets: syncTickets,
        };
        if (hasCreds) body.credentials = filled;
        await api.put(`/api/v1/psa-integrations/${editing.id}`, body);
      } else {
        if (!name || !baseUrl) {
          setError("Name and base URL are required");
          setBusy(false);
          return;
        }
        await api.post("/api/v1/psa-integrations", {
          name,
          provider,
          base_url: baseUrl,
          enabled,
          sync_companies: syncCompanies,
          sync_tickets: syncTickets,
          credentials: hasCreds ? filled : null,
        });
      }
      onSaved();
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-5 w-full max-w-lg space-y-3 max-h-[90vh] overflow-y-auto">
        <h3 className="text-lg font-semibold">
          {editing ? "Edit PSA integration" : "Add PSA integration"}
        </h3>

        <Field label="Name">
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full border rounded px-2 py-1.5 text-sm dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100"
            placeholder="Production ConnectWise"
          />
        </Field>

        <Field label="Provider">
          <select
            value={provider}
            onChange={(e) => setProvider(e.target.value as Provider)}
            className="w-full border rounded px-2 py-1.5 text-sm dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100 disabled:bg-gray-100 dark:disabled:bg-gray-700"
            disabled={!!editing}
          >
            <option value="connectwise">ConnectWise Manage</option>
            <option value="autotask">Autotask (Datto)</option>
            <option value="halo">HaloPSA</option>
          </select>
        </Field>

        <Field label="Base URL">
          <input
            value={baseUrl}
            onChange={(e) => setBaseUrl(e.target.value)}
            className="w-full border rounded px-2 py-1.5 text-sm font-mono dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100"
            placeholder={
              provider === "connectwise"
                ? "https://api-na.myconnectwise.net/v4_6_release/apis/3.0"
                : provider === "autotask"
                ? "https://webservicesN.autotask.net/atservicesrest/v1.0"
                : "https://myorg.halopsa.com"
            }
          />
        </Field>

        <div className="border-t dark:border-gray-700 pt-3">
          <div className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">
            Credentials{" "}
            {editing?.has_credentials && (
              <span className="font-normal text-gray-500 dark:text-gray-400">
                (stored — leave blank to keep existing)
              </span>
            )}
          </div>
          <div className="space-y-2">
            {CRED_FIELDS[provider].map((f) => (
              <Field key={f.key} label={f.label}>
                <input
                  type="password"
                  value={creds[f.key] || ""}
                  onChange={(e) =>
                    setCreds((c) => ({ ...c, [f.key]: e.target.value }))
                  }
                  className="w-full border rounded px-2 py-1.5 text-sm font-mono dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100"
                  autoComplete="new-password"
                />
              </Field>
            ))}
          </div>
        </div>

        <div className="flex flex-wrap gap-4 pt-1">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
            />
            Enabled
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={syncCompanies}
              onChange={(e) => setSyncCompanies(e.target.checked)}
            />
            Sync companies
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={syncTickets}
              onChange={(e) => setSyncTickets(e.target.checked)}
            />
            Sync tickets
          </label>
        </div>

        {error && (
          <div className="text-xs bg-red-50 dark:bg-red-900/40 text-red-800 dark:text-red-300 p-2 rounded">{error}</div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button
            onClick={onClose}
            className="px-3 py-1.5 border dark:border-gray-600 rounded text-sm hover:bg-gray-50 dark:hover:bg-gray-700/40"
          >
            Cancel
          </button>
          <button
            onClick={save}
            disabled={busy}
            className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm font-medium disabled:opacity-50"
          >
            {editing ? "Save" : "Create"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">{label}</label>
      {children}
    </div>
  );
}
